import React, { useState, useEffect, useContext } from 'react';
import api from '../../services/api';
import { CrmSettingsContext } from './CrmLayout';
import './Screens.css';
import './Settings.css';

// House appearance. One admin sets it, everyone sees it -- so the tool reads as one
// company system rather than eleven personal configurations.

const THEMES = [
  { value: 'graphite', name: 'Graphite',
    blurb: 'Quiet ink for actions, crimson kept for identity. The default.',
    swatch: ['#1c1c22', '#8e1329', '#c9a227'] },
  { value: 'crimson', name: 'Crimson',
    blurb: 'Brand forward — Fitness First red carries the interface.',
    swatch: ['#a01830', '#c41e3a', '#c9a227'] },
  { value: 'slate', name: 'Slate',
    blurb: 'Cool and conventional. The most familiar enterprise look.',
    swatch: ['#2f4a72', '#5b7aa8', '#8fa6c4'] },
  { value: 'field', name: 'Field',
    blurb: 'Turf green. Reads sporting rather than corporate.',
    swatch: ['#1f5133', '#3d7a55', '#8bbd9c'] },
];

const LANDINGS = [
  { value: 'dashboard', label: 'Dashboard', hint: "Who's coming up and who's slipping" },
  { value: 'pipeline', label: 'Pipeline', hint: 'The stage board' },
  { value: 'organizations', label: 'Accounts', hint: 'The account list' },
  { value: 'invoices', label: 'Invoices', hint: 'Billing and balances' },
  { value: 'reports', label: 'Reports', hint: 'Revenue and margin' },
];

const Settings = () => {
  const { refreshSettings, viewer } = useContext(CrmSettingsContext);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/crm/settings')
      .then(r => setForm(r.data.settings))
      .catch(err => setError(err.response?.data?.message || 'Failed to load settings'));
  }, []);

  const save = async (patch) => {
    const next = { ...form, ...patch };
    setForm(next);
    setSaved(false);
    try {
      setSaving(true);
      await api.put('/crm/settings', {
        theme: next.theme,
        appearance: next.appearance,
        density: next.density,
        default_landing: next.default_landing,
        use_team_colors: next.use_team_colors,
      });
      await refreshSettings();
      setSaved(true);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!viewer.canAdmin) {
    return (
      <div className="crm-screen">
        <div className="screen-header"><div><h1>Settings</h1></div></div>
        <div className="card">
          <p className="empty">
            <strong>Managers only</strong>
            The CRM's look is set once for everyone. Ask an administrator to change it.
          </p>
        </div>
      </div>
    );
  }
  if (error && !form) return <div className="crm-screen"><p className="banner error">{error}</p></div>;
  if (!form) return <div className="crm-screen"><p className="loading">Loading…</p></div>;

  return (
    <div className="crm-screen crm-settings">
      <div className="screen-header">
        <div>
          <h1>Settings</h1>
          <p className="subtitle">
            The house look. Changing it here changes it for everyone in the CRM.
          </p>
        </div>
        {saving ? <span className="save-state">Saving…</span>
          : saved ? <span className="save-state ok">Saved</span> : null}
      </div>

      {error && <p className="banner error">{error}</p>}

      <div className="card">
        <h2>Theme</h2>
        <div className="theme-grid">
          {THEMES.map(t => (
            <button
              key={t.value}
              type="button"
              className={`theme-card${form.theme === t.value ? ' selected' : ''}`}
              onClick={() => save({ theme: t.value })}
            >
              <span className="theme-swatch">
                {t.swatch.map(c => <span key={c} style={{ background: c }} />)}
              </span>
              <span className="theme-name">{t.name}</span>
              <span className="theme-blurb">{t.blurb}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="card">
        <h2>Appearance</h2>
        <div className="option-row">
          {['light', 'dark'].map(mode => (
            <button
              key={mode}
              type="button"
              className={`option-btn${form.appearance === mode ? ' selected' : ''}`}
              onClick={() => save({ appearance: mode })}
            >
              <span className={`mode-preview mode-${mode}`} />
              {mode === 'light' ? 'Light' : 'Dark'}
            </button>
          ))}
        </div>
      </div>

      <div className="card">
        <h2>Density</h2>
        <p className="setting-hint">How much room each row gets. Compact fits noticeably more on screen.</p>
        <div className="option-row">
          {[
            { value: 'comfortable', label: 'Comfortable' },
            { value: 'compact', label: 'Compact' },
          ].map(d => (
            <button
              key={d.value}
              type="button"
              className={`option-btn${form.density === d.value ? ' selected' : ''}`}
              onClick={() => save({ density: d.value })}
            >
              <span className={`density-preview density-${d.value}`}>
                <span /><span /><span />
              </span>
              {d.label}
            </button>
          ))}
        </div>
      </div>

      <div className="card">
        <h2>Team colors</h2>
        <p className="setting-hint">
          Show each school's colors on their account, pipeline cards, and lists. You recognize a
          school by its colors before you read the name — this is what the colors field is for.
        </p>
        <label className="switch-row">
          <input
            type="checkbox"
            checked={!!form.use_team_colors}
            onChange={(e) => save({ use_team_colors: e.target.checked })}
          />
          <span>Use team colors on records</span>
        </label>
      </div>

      <div className="card">
        <h2>Opening screen</h2>
        <p className="setting-hint">Where the CRM lands when someone opens it.</p>
        <div className="landing-list">
          {LANDINGS.map(l => (
            <button
              key={l.value}
              type="button"
              className={`landing-btn${form.default_landing === l.value ? ' selected' : ''}`}
              onClick={() => save({ default_landing: l.value })}
            >
              <strong>{l.label}</strong>
              <em>{l.hint}</em>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Settings;
